// Fixed-height slots where the reserved descender space overflows the iframe.
const CLIPPING_SIZES = new Set([
    "300x250", // ADSTEAM-2554 — box / immersive
    "320x50" //   ADSTEAM-2614 — mobile leaderboard
]);

/**
 * ADSTEAM-2554: 3rd-party CM360/DV360 display creatives ship an inline-block
 * `<ins class="dcmads" style="display:inline-block;width:300px;height:250px">`.
 * An inline-block element sits on the text baseline, so WebKit/Safari reserves
 * descender space below it — pushing a 300x250 creative to ~264px inside its
 * fixed 250px iframe and clipping the bottom of the ad.
 *
 * ADSTEAM-2614: the same creatives clip the 320x50 mobile leaderboard. That slot
 * is worse off than 300x250 — the creative is exactly as tall as its iframe, so
 * there is no slack at all and the reserved descender space both pushes the
 * creative down (visible gap above) and clips it (cut off below).
 *
 * Set `vertical-align:top` directly on the creative's `<ins.dcmads>` (only when
 * it isn't already), which removes it from baseline layout so it fits — the same
 * thing ad-ops apply per-creative in GAM, but for every creative automatically.
 *
 * Scoped to the fixed-height sizes that clip (fluid sizes absorb the descender)
 * and to friendly (same-origin) iframes — a graceful no-op under SafeFrame /
 * cross-origin, where the creative can't (and needn't) be reached.
 *
 * @param {HTMLIFrameElement} iframe The GPT-injected creative iframe.
 */
export default function fixInlineBlockBaseline(iframe: HTMLIFrameElement): void {
    const size = `${ iframe.getAttribute("width") }x${ iframe.getAttribute("height") }`;

    if (!CLIPPING_SIZES.has(size)) {
        return;
    }

    try {
        const doc = iframe.contentDocument;

        if (!doc) {
            return;
        }

        doc.querySelectorAll("ins.dcmads").forEach((ins) => {
            const el = ins as HTMLElement;

            // Only the inline-block <ins> sits on the baseline and needs the fix.
            // ad-ops apply the same vertical-align inline in GAM, so skip if present.
            if (el.style.display === "inline-block" && el.style.verticalAlign !== "top") {
                el.style.setProperty("vertical-align", "top");
            }
        });
    } catch (error) {
        // cross-origin (SafeFrame) iframe — nothing we can or should do here
    }
}
